import {
  data,
  isRouteErrorResponse,
  Link,
  LoaderFunctionArgs,
  MetaFunction,
  useLoaderData,
  useLocation,
  useRouteError,
} from "react-router";
import React, { useEffect } from "react";
import { getAccessToken } from "~/utils/ensure-authenticated.server";
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
} from "@apollo/client/errors";
import { getServerClient } from "~/graphql";
import { graphql } from "~/graphql/codegen";
import { AnnotateArticleDocument, type AnnotateArticleQuery } from "~/graphql/codegen/graphql";
import { GenericBoundary } from "~/components/error-boundaries/error-boundaries";
import Annotate from "~/components/annotate/annotate";
import { getDefaultArticleImage } from "~/utils/resource-icon";
import type { loader as RootLoader } from "../../root";
import usePageView from "~/hooks/page-view";
import { trackContentConsumed } from "~/components/upsell/upsell-tracking-utils";
import { Button } from "@mindstonehq/ui";
import { Feedback } from "~/components/feedback";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { currentSpaceUniqueName } from "~/hooks/check-authenticated";
import {
  fetchEventBackLink,
  type EventBackLink,
} from "~/lib/community-admin-client.server";

const ANNOTATE_ARTICLE_QUERY = graphql(`
  fragment evidencing on ResourceEvidencing {
    state
    totalPassiveEarnablePoints
    totalPassiveEarnedPoints
    totalTriviaEarnablePoints
    totalTriviaEarnedPoints
    totalEarnedPoints
    totalEarnablePoints
    passiveDetails {
      earnablePoints
      earnedPoints
      skill {
        id
        name
        description
        topSkillGroup {
          color
        }
      }
    }
    triviaDetails {
      earnedPoints
      skill {
        name
        topSkillGroup {
          color
        }
      }
    }
    score {
      total
    }
  }

  fragment Caption on Caption {
    state
    caption {
      text
      start
      duration
    }
  }

  fragment minProfile on Profile {
    __typename
    id
    email
    name: displayName
    displayName
    username
    profilePictureURL
    tagLine
    following
    mine
  }

  fragment ResourceComment on ResourceComment {
    id
    content
    profile {
      ...minProfile
    }
    canEdit
    createdAt
    resource {
      id
    }
  }

  fragment PDFComment on PDFComment {
    xfdfString
  }

  fragment AnnotationHighlightComment on AnnotationHighlightComment {
    id
    content
    canEdit
    createdAt
    profile {
      ...minProfile
    }
    resource {
      id
    }
    highlight {
      id
    }
  }

  fragment ResourceHTMLHighlight on HTMLHighlight {
    startOffset
    startParentID
    endOffset
    endParentID
    comments {
      ...AnnotationHighlightComment
    }
    privateNote {
      ...AnnotationHighlightComment
    }
  }

  fragment ResourcePDFHighlight on PDFHighlight {
    xfdfString
    page
    comments {
      ...AnnotationHighlightComment
      ...PDFComment
    }
    privateNote {
      ...AnnotationHighlightComment
      ...PDFComment
    }
  }

  fragment AnnotationHighlight on AnnotationHighlight {
    __typename
    id
    model
    color
    text
    prefix
    suffix
    canEdit
    profile {
      ...minProfile
    }
    createdAt
    ...ResourceHTMLHighlight
    ...ResourcePDFHighlight
  }

  fragment ResourceAnnotations on Annotations {
    __typename
    comments {
      ...ResourceComment
    }
    highlights {
      ...AnnotationHighlight
    }
  }

  fragment TriviaResult on TriviaResult {
    id
    question
    earnedTriviaPoints
    result
    perObjective {
      objective {
        id
        name
      }
      pointsEarned
    }
    answers {
      ... on TriviaMultipleChoiceResult {
        id
        result
        isUserAnswer
        answer
        explanation
      }
    }
  }

  fragment trivia on ResourceTrivia {
    earnablePoints
    questions {
      ... on ResourceTriviaMultipleChoiceQuestion {
        id
        question
        answers {
          id
          answer
        }
      }
    }
  }

  fragment FullResource on Resource {
    id
    headline
    mainImage
    type
    readingTime
    state
    url
    unsubscribed
    siteName
    publishedDate
    author
    fileID
    fileUrl
    url
    excerpt
    tags
    isOwner
    canEdit
    canArchive
    canDelete
    canRemove
    canMoveToInbox
    private
    evidencing {
      ...evidencing
    }
    trivia {
      ...trivia
    }
    relatedId
    shareReason
    version
    publishedUrl
    userProgress
    read
    following
    isInMindstream
    canAddToMindspace
    canAddToMindstream
    canShare
    userContext {
      userProgress {
        lastIndexID
        lastTimestamp
        percent
      }
    }
    location {
      __typename
      ... on Mindspace {
        name
      }
      ... on InLibrary {
        inLibrary
      }
    }
    suggested
    extensions {
      afterBlockIndex
      __typename
      ... on TriviaExtension {
        result {
          ...TriviaResult
        }
        question {
          __typename
          ... on ResourceTriviaMultipleChoiceQuestion {
            id
            question
            answers {
              id
              answer
            }
          }
        }
      }
    }
    editorContent
    url
    mindspaceId
    textContent
    annotations {
      ...ResourceAnnotations
    }
    following
    unsubscribed
    caption {
      ...Caption
    }
    coachHighlights {
      state
      shares {
        timestamp
        coach
        kind
        text
        url
      }
      questionsAnswered {
        timestamp
        question
        coach
        answer
      }
    }
  }

  query annotateArticle($id: ID!) {
    readResource(id: $id) {
      ...FullResource
    }
  }

  mutation unfollowResource($ids: [ID!]!) {
    unfollowResource(ids: $ids)
  }

  mutation updateResourceVisibility($id: ID!, $private: Boolean!) {
    setResourcePrivacy(body: { id: $id, private: $private }) {
      id
    }
  }
`);

// Classify the loader's caught error by terrors code (e.g.
// "forbidden.invalid_access"). Structured `extensions.code` on Apollo's error
// wrappers is checked first; the message-text fallback keeps detection working
// if a wrapper drops `extensions` (same resilience rationale as
// graphql/error-detection.ts).
function hasGraphqlErrorCode(error: unknown, token: string): boolean {
  const matches = (code: unknown, message: unknown) =>
    (typeof code === "string" &&
      (code === token || code.endsWith(`.${token}`))) ||
    (typeof message === "string" && message.includes(token));
  if (CombinedGraphQLErrors.is(error) || CombinedProtocolErrors.is(error)) {
    return error.errors.some((e) => matches(e.extensions?.code, e.message));
  }
  return error instanceof Error && matches(undefined, error.message);
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const articleId = params["articleId"];
  if (!articleId) {
    throw data(
      {
        success: false,
        error: "not found",
        article: null,
        eventBackLink: null,
      },
      404,
    );
  }
  const accessToken = await getAccessToken(request);
  try {
    const { data: articleData, error } = await getServerClient().query<AnnotateArticleQuery>({
      query: AnnotateArticleDocument,
      variables: { id: articleId },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    });
    if (error) {
      throw error;
    }

    if (!articleData?.readResource) {
      throw data(
        {
          success: false,
          error: "something went wrong",
          article: null,
          eventBackLink: null,
        },
        500,
      );
    }

    let eventBackLink: EventBackLink | null = null;
    try {
      const uniqueName = currentSpaceUniqueName(request);
      if (uniqueName === "community") {
        eventBackLink = await fetchEventBackLink({ articleId, request });
      }
    } catch (err) {
      console.warn("annotate_event_back_link_failed", {
        articleId,
        error: err instanceof Error ? err.message : String(err),
      });
      eventBackLink = null;
    }

    return {
      success: true,
      error: null,
      article: articleData.readResource,
      eventBackLink,
    };
  } catch (error: unknown) {
    if (hasGraphqlErrorCode(error, "not_found")) {
      throw data(
        {
          success: false,
          error: "not found",
          article: null,
          eventBackLink: null,
        },
        404,
      );
    }
    if (hasGraphqlErrorCode(error, "invalid_access")) {
      throw data(
        {
          success: false,
          error: "invalid_access",
          article: null,
          eventBackLink: null,
        },
        403,
      );
    }
    console.error("annotateerror:", error);
    throw data(
      {
        success: false,
        error: "something went wrong",
        article: null,
        eventBackLink: null,
      },
      500,
    );
  }
}

export const meta: MetaFunction<
  typeof loader,
  {
    root: typeof RootLoader;
  }
> = ({ data, matches }) => {
  if (!data) {
    return [
      {
        title: i18n._(msg`Mindstone`),
      },
    ];
  }
  const article = data.article;
  if (!article) {
    return [
      {
        title: i18n._(msg`Mindstone`),
      },
    ];
  }
  const rootData = matches.find((match) => match.id === "root");
  const uniqueName = rootData?.data.currentSpace.uniqueName || "app";
  const headline = article.headline || "";
  return [
    {
      title: i18n._(msg`${headline} | Mindstone`),
    },
    {
      name: "description",
      content: article.excerpt?.substring(0, 160),
    },
    {
      property: "og:title",
      content: i18n._(msg`${headline} | Mindstone`),
    },
    {
      property: "og:description",
      content: article.excerpt?.substring(0, 160),
    },
    { property: "og:site_name", content: i18n._(msg`Mindstone`) },
    {
      property: "og:url",
      content: `https://${uniqueName}.mindstone.com/annotate/${data.article.id}`,
    },
    {
      property: "og:type",
      content: "article",
    },
    {
      property: "og:image",
      content: getDefaultArticleImage(article.mainImage, article.type, article.state || ""),
    },
  ];
};

function isInvalidAccessRouteError(error: unknown): boolean {
  if (!isRouteErrorResponse(error) || error.status !== 403) return false;
  const payload = error.data;
  return (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    payload.error === "invalid_access"
  );
}

function AnnotateInvalidAccessBoundary() {
  const location = useLocation();
  const redirectUrl = `${location.pathname}${location.search}`;

  return (
    <Feedback
      code={403}
      title="Sign in to watch this recording"
      subtitle="This recording is available to signed-in members. Sign in to continue."
      cta={
        <Button variant="secondary" size="lg" asChild>
          <Link to={`/login?redirectUrl=${encodeURIComponent(redirectUrl)}`}>
            Sign in
          </Link>
        </Button>
      }
    />
  );
}

function EventBackLinkBanner({
  eventBackLink,
}: {
  eventBackLink: EventBackLink;
}) {
  return (
    <div className="mx-auto mt-4 w-full max-w-5xl px-4">
      <div className="flex flex-col gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <span className="font-medium">
            <Trans>From the event:</Trans>
          </span>{" "}
          <Link
            to={`/events/${eventBackLink.eventSlug}`}
            className="font-semibold underline-offset-2 hover:underline"
          >
            {eventBackLink.eventName}
          </Link>
          <span className="mx-2 text-emerald-600" aria-hidden="true">
            ·
          </span>
          <span>{eventBackLink.talkTitle}</span>
        </div>
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/events/${eventBackLink.eventSlug}`}>
            <Trans>View event</Trans>
          </Link>
        </Button>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isInvalidAccessRouteError(error)) {
    return <AnnotateInvalidAccessBoundary />;
  }

  return <GenericBoundary />;
}

export default function Index() {
  usePageView("Annotate");
  const result = useLoaderData<typeof loader>();

  // Track content consumption when user opens an article for reading
  useEffect(() => {
    if (result.success && result.article) {
      trackContentConsumed();
    }
  }, [result.success, result.article?.id]);

  if (!result.success) {
    throw new Error("Not a success");
  }
  return (
    <>
      {result.eventBackLink ? (
        <EventBackLinkBanner eventBackLink={result.eventBackLink} />
      ) : null}
      <Annotate loadedArticle={result.article} />
    </>
  );
}
